import * as React from "react";
import {styled, createTheme, ThemeProvider} from '@mui/material/styles';
import { useNavigate, useLocation } from "react-router-dom";
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ListItemButton from '@mui/material/ListItemButton';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Tooltip from '@mui/material/Tooltip';
import logo from './cc_logo_wide.svg';
import AppleIcon from '@mui/icons-material/Apple';
import AdbIcon from '@mui/icons-material/Adb';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
//side menu width
const drawerWidth = 195;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({theme, open}) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        })
    }),
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(7.3),
                },
            }),
        },
    }),
);

const theme = createTheme({
    palette: {
        primary: {
            main: '#272727', // Custom primary color
        },
        secondary: {
            main: '#fffa00', // Custom secondary color
        },
        background: {
            default: '#fafafa', // Custom background color
        },
        text: {
            primary: '#272727', // Primary text color
        },
    },
    typography: {
        fontFamily: '"Roboto", "Arial", sans-serif',
        h1: {
            fontSize: '2rem',
            fontWeight: 600,
        },
        h2: {
            fontSize: '1.5rem',
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
        },
    },

});

export const AppLayout = (props) => {
    const [open, setOpen] = React.useState(true);
    const concUser = JSON.parse(localStorage.getItem("concUser")) || {};
    const { email } = concUser;
    const navigate = useNavigate();
    const location = useLocation();

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const handleLogout = () => {
        localStorage.removeItem("concUser");
        navigate('/');
    };

    const renderMenuItem = (basePath, IconComponent, label) => {
        const selected = location.pathname.startsWith(basePath);

        return (
            <ListItemButton
                key={basePath}
                onClick={() => navigate(basePath)}
                selected={selected}
                sx={{
                    backgroundColor: selected ? '#f0f0f0' : 'inherit',
                    color: selected ? 'black' : 'inherit',
                    '&:hover': {
                        backgroundColor: '#e0e0e0',
                    },
                }}
            >
                <ListItemIcon sx={{ minWidth: '42px', color: selected ? 'black' : 'inherit' }}>
                    {IconComponent}
                </ListItemIcon>
                <ListItemText primary={label} />
            </ListItemButton>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{display: 'flex'}}>
                <AppBar position="absolute" open={open}>
                    <Toolbar
                        sx={{
                            pr: '24px', // keep right padding when drawer closed,
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            sx={{
                                marginRight: '36px',
                                ...(open && {display: 'none'}),
                            }}
                        >
                            <MenuIcon/>
                        </IconButton>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{flexGrow: 1}}
                            style={{textAlign: 'left'}}
                        >
                            {props.title}
                        </Typography>
                        <AccountCircleIcon />
                        <div style={{ textAlign: 'right', marginLeft: '5px' }}>
                           <Typography variant="body2">
                                {email}
                            </Typography>
                        </div>
                        <Tooltip title="Logout">
                            <IconButton color="inherit" onClick={handleLogout}>
                                <LogoutIcon/>
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                </AppBar>
                <Drawer variant="permanent" open={open} >
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            backgroundColor: '#272727',
                            px: [1],
                        }}
                    >
                        <img
                            src={logo}
                            alt="Conscious Connections logo"
                            style={{marginLeft: 'auto', marginRight: 'auto', maxWidth: '80%'}}
                        />
                        <IconButton onClick={toggleDrawer} sx={{ color: 'white' }}>
                            <ChevronLeftIcon/>
                        </IconButton>
                    </Toolbar>
                    <Divider />
                    <List component="nav">
                        {renderMenuItem('/dashboard', <DashboardOutlinedIcon />, 'Dashboard')}
                        {renderMenuItem('/users', <PeopleAltOutlinedIcon />, 'Users')}
                        {renderMenuItem('/events', <EventAvailableOutlinedIcon />, 'Events')}

                        <ListItemButton onClick={() => window.open('https://app.eu.amplitude.com/analytics/conscious-connections/dashboard/e-xbrurszb', '_blank', 'noopener,noreferrer')}>
                            <ListItemIcon sx={{ minWidth: 42 }}>
                                <AppleIcon />
                            </ListItemIcon>
                            <ListItemText primary="iOS Analytics" />
                        </ListItemButton>

                        <ListItemButton onClick={() => window.open('https://app.eu.amplitude.com/analytics/conscious-connections/dashboard/e-v1fuflcq', '_blank', 'noopener,noreferrer')}>
                            <ListItemIcon sx={{ minWidth: 42 }}>
                                <AdbIcon />
                            </ListItemIcon>
                            <ListItemText primary="Android Analytics" />
                        </ListItemButton>

                    </List>
                </Drawer>
                <Box
                    component="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}
                >
                    <Toolbar/>
                    <Container maxWidth="xxl" sx={{mt: 4, mb: 0}} style={{paddingLeft: '8px', paddingRight: '10px'}}>
                                <Paper sx={{p: 2, display: 'flex', flexDirection: 'column'}}>
                                    {props.page}
                                </Paper>
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};
